// Natural-language quick add (Kareem, 4 Nov: "Send steering pack to Dr. Amir Thursday 3pm, owner Sara, at risk,
// deliverable, in Strategy track" should just work). The deterministic part: it runs in the browser on every
// keystroke, and the fake model uses it too. Each recogniser takes a stretch of the text (a date, a time, "owner Sara",
// "in Strategy track", "at risk", a kind, "milestone"); what is left is the title. Only ids from the lists it is given
// ever come out. The model (POST /api/spaces/:id/quick-add/parse) fills what these rules miss.
import type { ActivityStatus, ActivityType } from './enums.js';

export interface QuickAddPerson { id: string; name: string }
export interface QuickAddTrack { id: string; name: string }
export interface QuickAddContext {
  /** Today in the team's time zone, YYYY-MM-DD. */
  today: string;
  tracks: QuickAddTrack[];
  /** Who can own an activity in this space. */
  people: QuickAddPerson[];
}
export const QUICK_FIELDS = ['title', 'type', 'date', 'time', 'owners', 'status', 'track', 'milestone'] as const;
export type QuickField = (typeof QUICK_FIELDS)[number];
export interface QuickAddDraft {
  title: string;
  type: ActivityType | null;
  /** YYYY-MM-DD */
  date: string | null;
  /** HH:MM, 24 hours */
  time: string | null;
  ownerIds: string[];
  status: ActivityStatus | null;
  trackId: string | null;
  isMilestone: boolean;
}
export interface QuickParse { draft: QuickAddDraft; found: QuickField[] }

export const emptyDraft = (): QuickAddDraft => ({ title: '', type: null, date: null, time: null, ownerIds: [], status: null, trackId: null, isMilestone: false });

// ---------- dates ----------
const DAY_MS = 86_400_000;
export function addDays(iso: string, n: number): string {
  return new Date(Date.parse(`${iso}T12:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}
const weekdayOf = (iso: string) => new Date(`${iso}T12:00:00Z`).getUTCDay();
const WEEKDAYS: [RegExp, number][] = [
  [/^sun(day)?$/, 0], [/^mon(day)?$/, 1], [/^tue(s|sday)?$/, 2], [/^wed(nesday)?$/, 3], [/^thu(r|rs|rsday)?$/, 4], [/^fri(day)?$/, 5], [/^sat(urday)?$/, 6],
];
// Sunday and Saturday only in full ("sun" and "sat" are words); the working days also short.
const WEEKDAY_RE = 'sunday|mon(?:day)?|tue(?:sday|s)?|wed(?:nesday)?|thu(?:rsday|rs|r)?|fri(?:day)?|saturday';
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_RE = 'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?';
const monthOf = (m: string) => MONTHS.indexOf(m.slice(0, 3).toLowerCase());

/** The coming day of that weekday, after today ("Thursday" on a Thursday is next week's). */
function coming(today: string, wd: number): string {
  const d = (wd - weekdayOf(today) + 7) % 7;
  return addDays(today, d === 0 ? 7 : d);
}
/** "next Thursday": the one in next week (the week starts on Sunday, as the team's working week does). */
function nextWeeks(today: string, wd: number): string {
  const c = coming(today, wd);
  const weekStart = addDays(today, -weekdayOf(today));
  return c < addDays(weekStart, 7) ? addDays(c, 7) : c;
}
/** A day and month (and maybe a year): this year, or next year when it has passed. null when it is not a real day. */
function dayMonth(today: string, day: number, month: number, year?: number): string | null {
  const y = year ?? Number(today.slice(0, 4));
  const iso = `${y}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime()) || d.getUTCMonth() !== month || d.getUTCDate() !== day) return null;
  if (year === undefined && iso < today) return dayMonth(today, day, month, y + 1);
  return iso;
}
const wd = (w: string) => WEEKDAYS.find(([re]) => re.test(w.toLowerCase()))?.[1] ?? -1;
const DATE_LEAD = String.raw`(?:(?:on|by|due|for|until|before)\s+)?`;

/** Date phrases, each: a pattern and how to read it. The first match that is a real day wins. */
const DATES: { re: RegExp; read: (m: RegExpExecArray, today: string) => string | null }[] = [
  { re: new RegExp(String.raw`\b${DATE_LEAD}(\d{4})-(\d{2})-(\d{2})\b`, 'i'), read: (m, t) => dayMonth(t, +m[3]!, +m[2]! - 1, +m[1]!) },
  { re: new RegExp(String.raw`\b${DATE_LEAD}(?:the\s+)?day after tomorrow\b`, 'i'), read: (_m, t) => addDays(t, 2) },
  { re: new RegExp(String.raw`\b${DATE_LEAD}(?:today|tonight|this (?:afternoon|evening|morning))\b`, 'i'), read: (_m, t) => t },
  { re: new RegExp(String.raw`\b${DATE_LEAD}(?:tomorrow|tmrw|tmr)\b`, 'i'), read: (_m, t) => addDays(t, 1) },
  { re: new RegExp(String.raw`\b${DATE_LEAD}next week\b`, 'i'), read: (_m, t) => addDays(t, 7 - weekdayOf(t)) },
  { re: new RegExp(String.raw`\b${DATE_LEAD}in (\d{1,2}|a|one|two|three) (day|week)s?\b`, 'i'), read: (m, t) => {
    const n = ({ a: 1, one: 1, two: 2, three: 3 } as Record<string, number>)[m[1]!.toLowerCase()] ?? Number(m[1]);
    return addDays(t, n * (m[2]!.toLowerCase() === 'week' ? 7 : 1));
  } },
  { re: new RegExp(String.raw`\b${DATE_LEAD}next (${WEEKDAY_RE})\b`, 'i'), read: (m, t) => nextWeeks(t, wd(m[1]!)) },
  { re: new RegExp(String.raw`\b${DATE_LEAD}(?:this |coming )?(${WEEKDAY_RE})\b`, 'i'), read: (m, t) => coming(t, wd(m[1]!)) },
  // 5 Nov, 5th November, 5 Nov 2026
  { re: new RegExp(String.raw`\b${DATE_LEAD}(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)?(?:\s+of)?\s+(${MONTH_RE})\.?(?:,?\s+(\d{4}))?\b`, 'i'), read: (m, t) => dayMonth(t, +m[1]!, monthOf(m[2]!), m[3] ? +m[3] : undefined) },
  // Nov 5, November 5th
  { re: new RegExp(String.raw`\b${DATE_LEAD}(${MONTH_RE})\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?\b`, 'i'), read: (m, t) => dayMonth(t, +m[2]!, monthOf(m[1]!), m[3] ? +m[3] : undefined) },
  // 5/11 (day first, as in Riyadh), 5/11/2026
  { re: new RegExp(String.raw`\b${DATE_LEAD}(\d{1,2})/(\d{1,2})(?:/(\d{4}))?\b`, 'i'), read: (m, t) => dayMonth(t, +m[1]!, +m[2]! - 1, m[3] ? +m[3] : undefined) },
];

// ---------- times ----------
const hhmm = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
const TIMES: { re: RegExp; read: (m: RegExpExecArray) => string | null }[] = [
  { re: /\b(?:at\s+|@\s*)?(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)(?![a-z])/i, read: m => {
    const h = +m[1]!, mm = m[2] ? +m[2] : 0;
    if (h < 1 || h > 12 || mm > 59) return null;
    const pm = m[3]!.toLowerCase().startsWith('p');
    return hhmm((h % 12) + (pm ? 12 : 0), mm);
  } },
  // 15:00; "5:30" without a zero is read as the afternoon, as "at 5" is.
  { re: /\b(?:at\s+)?([01]?\d|2[0-3]):([0-5]\d)\b/i, read: m => { const h = +m[1]!; return hhmm(m[1]!.length === 1 && h >= 1 && h <= 6 ? h + 12 : h, +m[2]!); } },
  { re: /\b(?:at\s+)?(noon|midday)\b/i, read: () => '12:00' },
  // "at 9": a working-day guess (1 to 6 is the afternoon, 7 to 11 the morning).
  { re: /\bat\s+(\d{1,2})(?![\d:/%]|\s*(?:days?|weeks?|hours?|minutes?|mins?|people|pages?)\b|(?:st|nd|rd|th)\b)/i, read: m => {
    const h = +m[1]!;
    if (h < 1 || h > 12) return null;
    return hhmm(h <= 6 ? h + 12 : h, 0);
  } },
];

// ---------- the rest ----------
const KINDS: Record<string, ActivityType> = { meeting: 'meeting', meet: 'meeting', call: 'meeting', workshop: 'workshop', deliverable: 'deliverable', task: 'task', todo: 'task' };
const STATUS_WORDS: [RegExp, ActivityStatus][] = [
  [/^at[\s-]risk$/i, 'at_risk'], [/^blocked$/i, 'blocked'], [/^in[\s-]progress$/i, 'in_progress'], [/^(started|ongoing|underway)$/i, 'in_progress'],
  [/^not[\s-](yet[\s-])?started$/i, 'not_started'], [/^(done|completed|finished)$/i, 'done'],
];
const statusOf = (s: string) => STATUS_WORDS.find(([re]) => re.test(s.trim()))?.[1] ?? null;
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** How a person can be named: the full name, the first name when no one else has it, and "Dr. Amir" forms. */
function personNames(people: QuickAddPerson[]): { id: string; name: string }[] {
  const out: { id: string; name: string }[] = [];
  const first = (n: string) => n.replace(/^(dr|mr|mrs|ms|eng)\.?\s+/i, '').split(/\s+/)[0] ?? '';
  const firsts = new Map<string, number>();
  for (const p of people) firsts.set(first(p.name).toLowerCase(), (firsts.get(first(p.name).toLowerCase()) ?? 0) + 1);
  for (const p of people) {
    out.push({ id: p.id, name: p.name });
    const f = first(p.name);
    if (f && firsts.get(f.toLowerCase()) === 1) out.push({ id: p.id, name: f });
  }
  return out.sort((a, b) => b.name.length - a.name.length);
}

/** The person named at the start of s (the longest name that fits), or null. */
function personAt(s: string, names: { id: string; name: string }[]): { id: string; len: number } | null {
  for (const n of names) {
    const re = new RegExp(`^${escapeRe(n.name)}(?![\\p{L}\\p{N}])`, 'iu');
    const m = re.exec(s);
    if (m) return { id: n.id, len: m[0].length };
  }
  return null;
}

interface Span { from: number; to: number }

/**
 * Read a quick-add line. Every keystroke: cheap, no network. found lists the fields it recognised (title when any
 * words are left). Ids come only from ctx.
 */
export function parseQuickAdd(text: string, ctx: QuickAddContext): QuickParse {
  const draft = emptyDraft();
  const found = new Set<QuickField>();
  const taken: Span[] = [];
  const free = (from: number, to: number) => !taken.some(t => from < t.to && to > t.from);
  const take = (from: number, to: number) => { taken.push({ from, to }); };
  /** The first match of re in the text that does not overlap what is already taken. */
  const find = (re: RegExp, from = 0): RegExpExecArray | null => {
    const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g');
    g.lastIndex = from;
    let m: RegExpExecArray | null;
    while ((m = g.exec(text))) {
      if (free(m.index, m.index + m[0].length)) return m;
      if (m[0].length === 0) g.lastIndex++;
    }
    return null;
  };
  const names = personNames(ctx.people);

  // Owners: "@Sara", "owner Sara", "owners Sara and Ali", "owned by Sara", "assign to Sara".
  for (const m of text.matchAll(/@/g)) {
    const at = m.index;
    if (!free(at, at + 1)) continue;
    const p = personAt(text.slice(at + 1), names);
    if (!p) continue;
    take(at, at + 1 + p.len);
    if (!draft.ownerIds.includes(p.id)) draft.ownerIds.push(p.id);
  }
  const ownerKw = /\b(?:owners?|owned by|assigned to|assign to|assignee|led by)\s*:?\s+/gi;
  for (const m of text.matchAll(ownerKw)) {
    if (!free(m.index, m.index + m[0].length)) continue;
    let pos = m.index + m[0].length;
    let end = -1;
    for (;;) {
      const p = personAt(text.slice(pos).replace(/^@/, ''), names);
      if (!p) break;
      const shift = text[pos] === '@' ? 1 : 0;
      pos += shift + p.len;
      end = pos;
      if (!draft.ownerIds.includes(p.id)) draft.ownerIds.push(p.id);
      const sep = /^\s*(?:,|and|&|\+)\s*/i.exec(text.slice(pos));
      if (!sep || !personAt(text.slice(pos + sep[0].length).replace(/^@/, ''), names)) break;
      pos += sep[0].length;
    }
    if (end > 0) take(m.index, end);
  }
  if (draft.ownerIds.length) found.add('owners');

  // Track: "in Strategy track", "in the Data Readiness track", "track: Strategy", "on Governance and Policy".
  const tracks = [...ctx.tracks].sort((a, b) => b.name.length - a.name.length);
  outer: for (const t of tracks) {
    const full = escapeRe(t.name);
    const firstWord = escapeRe(t.name.split(/\s+/)[0] ?? t.name);
    const res = [
      new RegExp(String.raw`\b(?:in|on|under|for)\s+(?:the\s+)?${full}(?:\s+track)?(?![\p{L}\p{N}])`, 'iu'),
      new RegExp(String.raw`\btrack\s*:?\s+${full}(?![\p{L}\p{N}])`, 'iu'),
      new RegExp(String.raw`\b(?:(?:in|on|under)\s+(?:the\s+)?)?${full}\s+track\b`, 'iu'),
      new RegExp(String.raw`\b(?:(?:in|on|under)\s+(?:the\s+)?)?${firstWord}\s+track\b`, 'iu'),
    ];
    for (const re of res) {
      const m = find(re);
      if (m) { take(m.index, m.index + m[0].length); draft.trackId = t.id; found.add('track'); break outer; }
    }
  }

  // Segments between commas: a segment that is only a kind, a status or "milestone".
  const segRe = /[^,;]+/g;
  for (const m of text.matchAll(segRe)) {
    const raw = m[0];
    const lead = raw.length - raw.trimStart().length;
    const seg = raw.trim();
    const from = m.index + lead, to = from + seg.length;
    if (!seg || !free(from, to)) continue;
    const s = seg.toLowerCase().replace(/^(?:it(?:'s| is)\s+|is\s+|as\s+|status\s*:?\s*|type\s*:?\s*|kind\s*:?\s*)/, '').replace(/^(?:an?|the)\s+/, '').replace(/[.!]+$/, '');
    if (/^(?:a\s+)?milestone$/.test(s)) { take(from, to); draft.isMilestone = true; found.add('milestone'); continue; }
    if (KINDS[s] && s !== 'call' && s !== 'meet' && from > 0) { take(from, to); draft.type = KINDS[s]!; found.add('type'); continue; }
    const st = statusOf(s);
    if (st && from > 0) { take(from, to); draft.status = st; found.add('status'); continue; }
  }
  // Status phrases anywhere: "at risk", "in progress", "not started", "status blocked", "is blocked".
  if (!draft.status) {
    const m = find(/\b(?:status\s*:?\s*|(?:it\s+)?is\s+)?(at[\s-]risk|in[\s-]progress|not[\s-](?:yet[\s-])?started)\b/i)
      ?? find(/\b(?:status\s*:?\s*|(?:it\s+)?is\s+)(blocked|done|in[\s-]progress)\b/i);
    if (m) { take(m.index, m.index + m[0].length); draft.status = statusOf(m[1]!); found.add('status'); }
  }
  // Kind phrases anywhere: "as a deliverable", "type workshop".
  if (!draft.type) {
    const m = find(/\b(?:as\s+an?|type\s*:?|kind\s*:?)\s+(meeting|workshop|deliverable|task)\b/i);
    if (m) { take(m.index, m.index + m[0].length); draft.type = KINDS[m[1]!.toLowerCase()]!; found.add('type'); }
  }
  if (!draft.isMilestone) {
    const m = find(/\b(?:as\s+an?|it(?:'s| is)\s+an?|is\s+an?)\s+milestone\b/i) ?? find(/\bmilestone\s*$/i);
    if (m) { take(m.index, m.index + m[0].length); draft.isMilestone = true; found.add('milestone'); }
  }

  // Date, then time (so "5 Nov" is a day, not 5 o'clock).
  for (const d of DATES) {
    const m = find(d.re);
    if (!m) continue;
    const v = d.read(m, ctx.today);
    if (!v) continue;
    take(m.index, m.index + m[0].length);
    draft.date = v; found.add('date');
    break;
  }
  for (const t of TIMES) {
    const m = find(t.re);
    if (!m) continue;
    const v = t.read(m);
    if (!v) continue;
    take(m.index, m.index + m[0].length);
    draft.time = v; found.add('time');
    break;
  }

  // The title: what is left, tidied (connectors left hanging at the ends dropped, commas closed up).
  taken.sort((a, b) => a.from - b.from);
  let rest = '', at = 0;
  for (const t of taken) { if (t.from > at) rest += text.slice(at, t.from); rest += ' '; at = Math.max(at, t.to); }
  rest += text.slice(at);
  const title = tidyTitle(rest);
  draft.title = title;
  if (title) found.add('title');

  // The kind from the title's first word ("Workshop with IT", "Meeting with Hani"), left in the title.
  if (!draft.type) {
    const w = (title.split(/\s+/)[0] ?? '').toLowerCase();
    if (w === 'meeting' || w === 'workshop' || w === 'deliverable') { draft.type = KINDS[w]!; found.add('type'); }
  }
  return { draft, found: QUICK_FIELDS.filter(f => found.has(f)) };
}

const HANGING = /^(?:on|at|by|in|for|the|to|and|with|due|owner|owners|,)$/i;
export function tidyTitle(s: string): string {
  let t = s.replace(/\s+/g, ' ').replace(/\s*(?:,\s*)+/g, ', ').trim();
  // Drop connectors and commas left at either end, repeatedly.
  for (let i = 0; i < 6; i++) {
    const before = t;
    t = t.replace(/^[,;:\s-]+|[,;:\s-]+$/g, '');
    const words = t.split(' ');
    while (words.length > 1 && HANGING.test(words[words.length - 1]!)) words.pop();
    while (words.length > 1 && /^(?:on|at|by|and|,)$/i.test(words[0]!)) words.shift();
    t = words.join(' ');
    if (t === before) break;
  }
  // A comma left before a connector in the middle ("Call Ali, and") reads fine; one before nothing is gone above.
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
}

/**
 * The model's reading merged into the local one: what the person changed by hand wins, then what the rules found,
 * then the model (a field the rules did not find). The title is the model's only when the rules found other fields
 * the model also saw, so a title the rules left long ("... Sara will own it") gets the model's shorter one.
 */
export function mergeQuick(local: QuickParse, model: Partial<QuickAddDraft> | null): QuickAddDraft {
  const d = { ...local.draft, ownerIds: [...local.draft.ownerIds] };
  if (!model) return d;
  const has = (f: QuickField) => local.found.includes(f);
  if (!has('type') && model.type) d.type = model.type;
  if (!has('date') && model.date) d.date = model.date;
  if (!has('time') && model.time) d.time = model.time;
  if (!has('owners') && model.ownerIds?.length) d.ownerIds = [...model.ownerIds];
  if (!has('status') && model.status) d.status = model.status;
  if (!has('track') && model.trackId) d.trackId = model.trackId;
  if (!has('milestone') && model.isMilestone) d.isMilestone = true;
  if (model.title && model.title.trim()) {
    const extra = (['type', 'date', 'time', 'owners', 'status', 'track'] as const).some(f => !has(f) && (f === 'owners' ? !!model.ownerIds?.length : f === 'track' ? !!model.trackId : !!model[f]));
    // The model read more than the rules: its title leaves out what it read.
    if (extra || !d.title) d.title = model.title.trim();
  }
  return d;
}
